import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import { authorsApiSlice } from './api/authors-api/authors-api';
import { brandsApiSlice } from './api/brands-api/brands-api';
import { couponsApiSlice } from './api/coupons-api‎/coupons-api‎';
import { dataExportsApiSlice } from './api/data-exports-api/data-exports-api';
import { domainsApiSlice } from './api/domains-api/domains-api';
import { melApiSlice } from './api/mel-api/mel-api';
import { pageStatusesApiSlice } from './api/page-statuses-api/page-statuses-api';
import { pagesApiSlice } from './api/pages-api/pages-api';
import { productLinksApiSlice } from './api/product-links-api/product-links-api';
import { productsApiSlice } from './api/products-api/products-api';
import { publishersApiSlice } from './api/publishers-api/publishers-api';
import { getRtkQueryMiddlewares } from './middlewares/rtk-query-middlewares';
import { authorReducer } from './reducers/AuthorReducer';
import { reducer as confirmModalReducer } from './reducers/common/confirm-modal-slice';
import { reducer as domainListReducer } from './reducers/common/domain-list-slice';
import { reducer as publisherImagesListReducer } from './reducers/common/publisher-images-list-slice';
import { reducer as getCompareProductTableReducer } from './reducers/compare-product-table/get-product-compare-table';
// eslint-disable-next-line max-len
import { reducer as updateCompareProductTableReducer } from './reducers/compare-product-table/update-product-compare-table';
import { reducer as couponAttachingReducer } from './reducers/coupons/couponAttachingReducer';
import { reducer as createCouponReducer } from './reducers/coupons/createCreateCouponReducer';
import { reducer as getCouponsListReducer } from './reducers/coupons/getCouponListReducer';
import { reducer as getProductsCouponListReducer } from './reducers/coupons/getProductsCouponListReducer';
import { reducer as updateCouponReducer } from './reducers/coupons/updateCouponReducer';
import { reducer as dailyDealsReducer } from './reducers/daily-deals/daily-deals';
import { orderReducer } from './reducers/OrderReducer';
import { reducer as orderStatusReducer } from './reducers/orders/orderStatusSlice';
import { reducer as refundOrderReducer } from './reducers/orders/refundOrderSlice';
import { aiImagesGenerationStatusSlice } from './reducers/pages/ai-images-generation';
import { reducer as brandListReducer } from './reducers/pages/brand-list-slice';
import { reducer as deletePageReducer } from './reducers/pages/delete-page-slice';
import { reducer as pageStatusesListReducer } from './reducers/pages/page-statuses-list-slice';
import { reducer as pagesListReducer } from './reducers/pages/pages-list-slice';
import { reducer as publisherListReducer } from './reducers/pages/publisher-list-slice';
import { reducer as checkingKeywordReducer } from './reducers/products/checkKeywordSlice';
import { generateTemporaryLinksSlice } from './reducers/temporary-links/generate-temporary-links-slice';
import { reducer as temporaryLinksReducer } from './reducers/temporary-links/temporary-links-slice';
import rootSaga from './sagas/root-saga';

const sagaMiddleware = createSagaMiddleware();

const rootReducer = combineReducers({
  authorReducer,
  common: combineReducers({
    confirmModal: confirmModalReducer,
    domains: combineReducers({
      domainList: domainListReducer,
    }),
  }),
  compareProductTable: combineReducers({
    getCompareProductTable: getCompareProductTableReducer,
    updateCompareProductTable: updateCompareProductTableReducer,
  }),
  couponsReducer: combineReducers({
    couponAttaching: couponAttachingReducer,
    createCoupon: createCouponReducer,
    getList: getCouponsListReducer,
    getProductsCouponList: getProductsCouponListReducer,
    updateCoupon: updateCouponReducer,
  }),
  dailyDeals: dailyDealsReducer,
  orderReducer,
  orderStatus: orderStatusReducer,
  pages: combineReducers({
    brandList: brandListReducer,
    deletePage: deletePageReducer,
    pageStatusesList: pageStatusesListReducer,
    pagesList: pagesListReducer,
    publisherList: publisherListReducer,
  }),
  products: combineReducers({
    checkingKeyword: checkingKeywordReducer,
  }),
  publisherImagesList: publisherImagesListReducer,
  refundOrder: refundOrderReducer,
  [generateTemporaryLinksSlice.name]: generateTemporaryLinksSlice.reducer,
  [aiImagesGenerationStatusSlice.name]: aiImagesGenerationStatusSlice.reducer,
  temporaryLinks: temporaryLinksReducer,
  [melApiSlice.reducerPath]: melApiSlice.reducer,
  [authorsApiSlice.reducerPath]: authorsApiSlice.reducer,
  [brandsApiSlice.reducerPath]: brandsApiSlice.reducer,
  [domainsApiSlice.reducerPath]: domainsApiSlice.reducer,
  [publishersApiSlice.reducerPath]: publishersApiSlice.reducer,
  [pagesApiSlice.reducerPath]: pagesApiSlice.reducer,
  [productsApiSlice.reducerPath]: productsApiSlice.reducer,
  [couponsApiSlice.reducerPath]: couponsApiSlice.reducer,
  [productLinksApiSlice.reducerPath]: productLinksApiSlice.reducer,
  [dataExportsApiSlice.reducerPath]: dataExportsApiSlice.reducer,
  [pageStatusesApiSlice.reducerPath]: pageStatusesApiSlice.reducer,
});

export const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(sagaMiddleware, ...getRtkQueryMiddlewares()),
  reducer: rootReducer,
});

sagaMiddleware.run(rootSaga);

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;
